import { useReducer } from 'react';

import { IAction } from 'src/utils/ts-utilities';
import { CredentialTypeEnum } from 'src/graphql/schema';

enum ActionEnum {
  SET_INITIAL_DATA = 'set-initial-data',
  SET_STEP = 'set-step',
  SET_CREDENTIAL_TYPE = 'set-credential-type',
  GET_BACK = 'get-back',
}

const steps = ['notice', 'select-type', 'credential-form'] as const;

type IStep = (typeof steps)[number];

type IActionReducer =
  | IAction<ActionEnum.SET_INITIAL_DATA>
  | IAction<ActionEnum.GET_BACK>
  | IAction<ActionEnum.SET_STEP, { step: IStep }>
  | IAction<ActionEnum.SET_CREDENTIAL_TYPE, { credentialType: CredentialTypeEnum }>;

type IState = {
  step: IStep | null;
  credentialType: CredentialTypeEnum | null;
  history: IStep[];
};

const initialState: IState = {
  step: null,
  credentialType: null,
  history: [],
};

const reducer = (state: IState, action: IActionReducer): IState => {
  switch (action.type) {
    case ActionEnum.SET_STEP:
      return {
        ...state,
        step: action.payload.step,
        history: [...state.history, action.payload.step],
      };
    case ActionEnum.SET_CREDENTIAL_TYPE:
      return {
        ...state,
        step: 'credential-form',
        credentialType: action.payload.credentialType,
        history: [...state.history, 'credential-form'],
      };
    case ActionEnum.GET_BACK: {
      const history = [...state.history];
      history.pop();

      return {
        ...state,
        step: history[history.length - 1] ?? null,
        history,
      };
    }
    case ActionEnum.SET_INITIAL_DATA:
      return initialState;
    default:
      return state;
  }
};

export const useAddCredentialSlice = () => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setInitialData = () => dispatch({ type: ActionEnum.SET_INITIAL_DATA, payload: {} });

  const setStep = (step: IStep) => dispatch({ type: ActionEnum.SET_STEP, payload: { step } });

  const setCredentialType = (credentialType: CredentialTypeEnum) =>
    dispatch({ type: ActionEnum.SET_CREDENTIAL_TYPE, payload: { credentialType } });

  const getBack = () => dispatch({ type: ActionEnum.GET_BACK, payload: {} });

  return {
    ...state,
    setInitialData,
    setStep,
    setCredentialType,
    getBack,
  };
};
